import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { ramp } from '../components/Avatar'
import { Fold } from '../components/Fold'

/*
  One meeting, read as minutes.

  Everything on this screen has been in the database since the first meeting ran, and none
  of it had anywhere to go. The minutes hold the topic, the tally, the decision and the
  recorded dissent; member_responses holds what each seat said, in each round. There was no
  screen in the product that opened a completed meeting, so the best-rendered Boardium
  meeting anywhere was the sample on the signed-out marketing page.

  The treatment is that page's, brought inside: a named speaker column, the argument at a
  readable size and measure, the vote stated plainly, and the dissent in its own block. A
  meeting is a document. It should look like one.
*/

interface Turn {
  displayName: string
  archetype: string | null
  seatRole: string | null
  avatarUrl: string | null
  colorRamp: string | null
  round: number
  position: string
  stance: string | null
}

interface Item {
  id: string
  sequence: number
  topic: string | null
  turns: Turn[]
}

interface Commitment {
  text?: string
  owner?: string
  due_in_days?: number
  success_metric?: string
}

interface Minutes {
  topic?: string
  decisions?: string[]
  reasoning?: string
  recommendation?: string
  recorded_dissent?: string
  commitments?: Commitment[]
  data_requests?: { what?: string; why?: string }[]
  outcomes?: ({ item?: number; heading?: string; recommendation?: string } | string)[]
  tally?: Record<string, number>
}

interface Transcript {
  id: string
  status: string
  completedAt: string | null
  minutes: Minutes | null
  items: Item[]
}

export function MeetingMinutes({ meetingId, onBack }: { meetingId: string; onBack: () => void }) {
  const [data, setData] = useState<Transcript | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    void api.get<Transcript>(`/api/meetings/${meetingId}/transcript`)
      .then(setData)
      .catch(() => setFailed(true))
  }, [meetingId])

  if (failed) {
    return (
      <>
        <button className="linkish minutes__back" onClick={onBack}>← All meetings</button>
        <div className="empty">
          <div className="empty__title">That meeting could not be opened</div>
          <p>It may still be running. Completed meetings stay here for good.</p>
        </div>
      </>
    )
  }

  if (!data) return <div className="skeleton" style={{ width: '55%' }} />

  const m = data.minutes

  return (
    <article className="minutes">
      <button className="linkish minutes__back" onClick={onBack}>← All meetings</button>

      <header className="minutes__head">
        <p className="minutes__eyebrow">Minutes</p>
        <h1 className="screen-title">
          {data.completedAt
            ? new Date(data.completedAt).toLocaleDateString(undefined, {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
              })
            : 'This meeting has not finished'}
        </h1>
      </header>

      {/*
        The question stays open; the argument folds.

        A five-seat board on a four-item agenda puts twenty speeches on this page, and the
        chair's recommendation sits under all of them. Somebody opening last Friday's
        minutes wants to know what was decided, and was made to scroll the whole debate to
        find out. Folding the turns makes the page a list of what was discussed with the
        verdict directly under it, and the argument is one tap away per item.

        What is not folded is the item itself. Which questions went to the board is the
        shape of the meeting, and a page of collapsed rows saying "What the board said"
        five times would say nothing at all.
      */}
      {data.items.map(item => {
        const first = item.turns.filter(t => t.round === 1)
        const later = item.turns.filter(t => t.round > 1)

        return (
          <section key={item.id} className="script">
            <Topic raw={item.topic ?? m?.topic ?? null} />

            {first.length > 0 && (
              <Fold
                title="What the board said"
                meta={`${words(first.length)} ${first.length === 1 ? 'seat' : 'seats'}`}
              >
                {first.map((t, i) => <Turn key={i} t={t} />)}

                {/*
                  Round one is the argument. Later rounds are the board reacting to itself,
                  which is worth reading but is not what somebody opening this came for, so
                  it stays folded inside the fold.
                */}
                {later.length > 0 && (
                  <details className="disclose">
                    <summary className="disclose__head">
                      What they said after hearing each other
                    </summary>
                    <div className="script__second">
                      {later.map((t, i) => <Turn key={i} t={t} />)}
                    </div>
                  </details>
                )}
              </Fold>
            )}
          </section>
        )
      })}

      {m && (
        <section className="verdict">
          {m.decisions?.[0] && (
            <>
              <p className="verdict__vote">{voteLine(m.tally)}</p>
              <div className="verdict__body">
                {m.decisions[0].split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </>
          )}

          {/*
            The agenda, answered back one item at a time.

            The chair was always doing this work. It arrived as a single four hundred word
            string with the items numbered inside it, which meant the owner who raised ten
            separate worries had to read a wall of prose and find his own question in it.
            Same content, addressable. Anything written before the chair returned outcomes
            has none, and the verdict above still carries the whole answer, so an older
            meeting reads exactly as it always did.
          */}
          {itemOutcomes(m.outcomes).length > 0 && (
            <div className="itemverdicts">
              <h2 className="section-title">Item by item</h2>
              <ol className="itemverdicts__list">
                {itemOutcomes(m.outcomes).map((o, i) => (
                  <li className="itemverdict" key={i}>
                    <span className="itemverdict__n" aria-hidden>{o.item ?? i + 1}</span>
                    <div className="itemverdict__body">
                      <h3 className="itemverdict__head">{o.heading}</h3>
                      <p className="itemverdict__rec">{o.recommendation}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {m.recorded_dissent && (
            <div className="dissentblock">
              <span className="dissentblock__label">Recorded dissent</span>
              <p>{m.recorded_dissent}</p>
            </div>
          )}

          {m.reasoning && (
            <details className="disclose">
              <summary className="disclose__head">Why the chair landed there</summary>
              <div className="disclose__body"><p>{m.reasoning}</p></div>
            </details>
          )}
        </section>
      )}

      {/*
        What the meeting leaves behind. A decision on its own is a conversation; these two
        lists are what make it a board. They are read from the minutes rather than fetched,
        so they are exactly what was recorded on the day even if a commitment has since been
        closed or an agenda item has moved on.
      */}
      {(m?.commitments?.length ?? 0) > 0 && (
        <section className="outcome">
          <h2 className="section-title">What you committed to</h2>
          <ol className="outcome__list">
            {m!.commitments!.map((c, i) => (
              <li key={i}>
                <span className="outcome__what">{c.text}</span>
                <span className="outcome__meta">
                  {c.owner ? `${c.owner} · ` : ''}
                  {typeof c.due_in_days === 'number'
                    ? `within ${c.due_in_days} ${c.due_in_days === 1 ? 'day' : 'days'}`
                    : 'no date'}
                  {c.success_metric ? ` · ${c.success_metric}` : ''}
                </span>
              </li>
            ))}
          </ol>
          <p className="outcome__foot">
            These are on <a className="inline-link" href="#commitments">Actions</a>, and the
            board will ask about them.
          </p>
        </section>
      )}

      {(m?.data_requests?.length ?? 0) > 0 && (
        <section className="outcome">
          <h2 className="section-title">Carried to the next meeting</h2>
          <ol className="outcome__list">
            {m!.data_requests!.map((r, i) => (
              <li key={i}>
                <span className="outcome__what">{r.what}</span>
                {r.why && <span className="outcome__meta">{r.why}</span>}
              </li>
            ))}
          </ol>
          <p className="outcome__foot">
            The board put these on <a className="inline-link" href="#table">The Table</a>{' '}
            itself. Answer them before Friday and the next meeting starts from your numbers
            rather than from what it had to assume.
          </p>
        </section>
      )}
    </article>
  )
}

/*
  What was put to the board.

  The agenda is built by MeetingAgendaService.Compose and it has real structure in it:
  "## " for a cluster label, "[n] " for a numbered item, "- " for a bullet, blank lines
  between paragraphs and hard wraps inside them. All of that was being thrown away. The old
  reader split on every newline and rendered each line as one paragraph in one size, which
  turned a hard wrap into a paragraph break, demoted every heading to body, and left two
  screens of undifferentiated prose with no way into it.

  So it is parsed rather than flattened. The distinction that matters to somebody reading
  their own minutes is that the numbered items are the agenda and the prepared background
  under each one is supporting material. Those two want to look different, because the
  second is most of the page and the first is the reason you opened it.
*/

type Block =
  | { kind: 'para';    text: string; prep: boolean }
  | { kind: 'heading'; text: string }
  | { kind: 'item';    n: string; text: string }
  | { kind: 'note';    text: string }
  | { kind: 'leadin';  text: string }
  | { kind: 'bullets'; items: string[] }

const PREP_HEADING = /^prepared background/i

/** A bullet's leading "[kind]" tag, which the memory rows carry and the others do not. */
function splitTag(line: string): { tag?: string; text: string } {
  const m = line.match(/^\[([a-z_]+)\]\s*(.+)$/i)
  return m ? { tag: m[1].replace(/_/g, ' '), text: m[2] } : { text: line }
}

function parseTopic(raw: string) {
  const blocks: Block[] = []
  const asked: { q: string; a: string }[] = []

  let para: string[] = []
  let bullets: string[] = []
  let prep = false

  // Soft wraps are joined with a space. A blank line is what actually ends a paragraph, and
  // treating every newline as one is what split "Do not let it slide into a" from "general
  // discussion: establish what actually happened first."
  const flushPara = () => {
    if (para.length === 0) return
    blocks.push({ kind: 'para', text: para.join(' '), prep })
    para = []
  }
  const flushBullets = () => {
    if (bullets.length === 0) return
    blocks.push({ kind: 'bullets', items: bullets })
    bullets = []
  }
  const flush = () => { flushPara(); flushBullets() }

  for (const rawLine of raw.split(/\r?\n/)) {
    const line = rawLine.trim()

    if (line === '') { flush(); continue }

    // The clarifier exchange, which keeps the question-and-answer presentation below.
    const q = line.match(/^asked:\s*(.+)$/i)
    if (q) { flush(); asked.push({ q: q[1], a: '' }); continue }

    const a = line.match(/^owner answered:\s*(.+)$/i)
    if (a) { flush(); if (asked.length > 0) asked[asked.length - 1].a = a[1]; continue }

    if (/^the owner has put this on the agenda:?$/i.test(line)) continue

    const heading = line.match(/^#{1,6}\s*(.+)$/)
    if (heading) {
      flush()
      prep = PREP_HEADING.test(heading[1])
      // "Prepared background for this item" labels the block rather than heading a section,
      // so the panel below carries the words and this does not repeat them.
      if (!prep) blocks.push({ kind: 'heading', text: heading[1] })
      continue
    }

    const item = line.match(/^\[(\d+)\]\s*(.+)$/)
    if (item) {
      flush()
      prep = false
      blocks.push({ kind: 'item', n: item[1], text: item[2] })
      continue
    }

    if (line.startsWith('- ')) { flushPara(); bullets.push(line.slice(2)); continue }

    if (/^\(.*\)$/.test(line)) { flush(); blocks.push({ kind: 'note', text: line.slice(1, -1) }); continue }

    // Inside the prepared background a short line ending in a colon introduces the bullets
    // under it: "What this business has told the board before:", "Open commitments on the
    // same ground:", "Recent numbers:".
    if (prep && line.endsWith(':') && line.length < 80) {
      flush()
      blocks.push({ kind: 'leadin', text: line.slice(0, -1) })
      continue
    }

    para.push(line)
  }

  flush()

  // A board-raised topic repeats its heading as the first line of body. Said once.
  for (let i = 1; i < blocks.length; i++) {
    const prev = blocks[i - 1]
    const cur = blocks[i]
    if (prev.kind === 'heading' && cur.kind === 'para' && cur.text === prev.text) {
      blocks.splice(i, 1)
      i--
    }
  }

  return { blocks, asked }
}

function Topic({ raw }: { raw: string | null }) {
  if (!raw) return null

  const { blocks, asked } = parseTopic(raw)
  if (blocks.length === 0 && asked.length === 0) return null

  /*
    Consecutive prepared-background blocks are wrapped together rather than styled one at a
    time, so the supporting material reads as one recessed panel under the item it belongs
    to, rather than as more paragraphs in the same column.
  */
  const runs: { prep: boolean; blocks: Block[] }[] = []
  for (const b of blocks) {
    const isPrep = (b.kind === 'para' && b.prep) || b.kind === 'leadin' || b.kind === 'bullets'
    const last = runs[runs.length - 1]
    if (last && last.prep === isPrep) last.blocks.push(b)
    else runs.push({ prep: isPrep, blocks: [b] })
  }

  return (
    <div className="script__q">
      <span className="script__label">On the table</span>

      {runs.map((run, i) =>
        run.prep ? (
          <aside key={i} className="prep">
            <span className="prep__label">Prepared background</span>
            {run.blocks.map((b, j) => <Piece key={j} b={b} />)}
          </aside>
        ) : (
          <div key={i}>{run.blocks.map((b, j) => <Piece key={j} b={b} />)}</div>
        ),
      )}

      {asked.length > 0 && (
        <dl className="script__asked">
          {asked.map((x, i) => (
            <div key={i}>
              <dt>The board asked</dt>
              <dd>{x.q}</dd>
              {x.a && <><dt>You answered</dt><dd>{x.a}</dd></>}
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

function Piece({ b }: { b: Block }) {
  if (b.kind === 'heading') return <h3 className="script__cluster">{b.text}</h3>

  if (b.kind === 'item') {
    return (
      <div className="script__item">
        <span className="script__num" aria-hidden>{b.n}</span>
        <p className="script__itemtext">{b.text}</p>
      </div>
    )
  }

  if (b.kind === 'note') return <p className="script__note">{b.text}</p>

  if (b.kind === 'leadin') return <p className="prep__leadin">{b.text}</p>

  if (b.kind === 'bullets') {
    return (
      <ul className="prep__list">
        {b.items.map((line, i) => {
          const { tag, text } = splitTag(line)
          // "… | measured by: X" is the commitment's own test, and it reads as a footnote to
          // the commitment rather than as more of the same sentence.
          const [body, measure] = text.split(/\s*\|\s*measured by:\s*/)
          return (
            <li key={i}>
              {tag && <span className="prep__tag">{tag}</span>}
              {body}
              {measure && <span className="prep__measure">Measured by: {measure}</span>}
            </li>
          )
        })}
      </ul>
    )
  }

  return <p className={b.prep ? 'prep__para' : 'script__topic'}>{b.text}</p>
}

function Turn({ t }: { t: Turn }) {
  return (
    <div className="turn" style={t.colorRamp ? ramp(t.colorRamp) : undefined}>
      <div className="turn__who">
        {t.avatarUrl
          ? <img className="turn__face" src={t.avatarUrl} alt="" loading="lazy" />
          : <span className="turn__face turn__face--blank" aria-hidden />}
        <div>
          <span className="turn__name">{t.displayName}</span>
          <span className="turn__role">{t.seatRole === 'dissenter' ? 'dissent' : t.archetype}</span>
          {t.stance && <span className={`turn__stance turn__stance--${t.stance}`}>{t.stance}</span>}
        </div>
      </div>

      <div className="turn__body">
        {t.position.split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  )
}

/**
 * "Carried, four to one." Built from the tally the board recorded rather than counted here,
 * so the sentence cannot disagree with the stances printed above it.
 */
/**
 * Only the entries that carry both halves.
 *
 * `outcomes` held bare strings before the chair started returning objects, and a handful of
 * meetings recorded it as an empty array for a year, so this reads defensively rather than
 * trusting the shape. An entry with a heading and no recommendation is the owner's own
 * question read back to him, which is worse than showing nothing.
 */
function itemOutcomes(
  outcomes?: ({ item?: number; heading?: string; recommendation?: string } | string)[],
): { item?: number; heading: string; recommendation: string }[] {
  if (!Array.isArray(outcomes)) return []

  return outcomes.flatMap(o => {
    if (typeof o !== 'object' || o === null) return []
    const heading = o.heading?.trim()
    const recommendation = o.recommendation?.trim()
    return heading && recommendation ? [{ item: o.item, heading, recommendation }] : []
  })
}

function voteLine(tally?: Record<string, number>): string {
  if (!tally) return 'The board decided.'

  const support = tally.support ?? 0
  const oppose = tally.oppose ?? 0
  const conditional = tally.conditional ?? 0

  if (oppose === 0 && conditional === 0 && support > 0) {
    return support === 1 ? 'Carried, one seat.' : `Carried, all ${support}.`
  }
  if (conditional > 0 && support === 0 && oppose === 0) {
    return conditional === 1
      ? 'Carried on one condition.'
      : `Carried, ${words(conditional)} conditional.`
  }
  /*
    The chair synthesises; it does not count votes and then obey them. So a recommendation
    can stand against the room, and until now that rendered as "Carried, two to three",
    which reads like arithmetic nobody checked. Worth saying plainly instead.

    This only became reachable when the seats started using the whole stance enum. Before
    that every meeting in the product tallied support 0, oppose 0, conditional 5.
  */
  if (oppose > support + conditional) {
    return oppose === 1
      ? 'The chair carried it over one objection.'
      : `The chair carried it over ${words(oppose)} objections.`
  }

  if (oppose > 0) return `Carried, ${words(support + conditional)} to ${words(oppose)}.`

  return `Carried, ${words(support)} for and ${words(conditional)} conditional.`
}

const NUMBERS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight']
function words(n: number): string { return NUMBERS[n] ?? String(n) }
